import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, Flag, Target } from "lucide-react";
import { fmtDate, programLabel } from "./programUtils";

function Metric({ label, value, tone = "text-slate-900", testId }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2.5" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-lg font-semibold tabular-nums leading-tight mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}

function focusLine(stageName, nextStage, program) {
  const s = (stageName || "").toLowerCase();
  if (s.includes("fta")) return "FTA reached — remaining items are ongoing training and compliance.";
  if (s.includes("career")) return "Career Agent focus: remaining training requirements and progression to FTA.";
  if (s.includes("reserve")) return `Path to Career conversion${program?.enrollment?.projected_appointment_date ? ` (target ${fmtDate(program.enrollment.projected_appointment_date)})` : ""}.`;
  return nextStage ? `Next recruiting milestone: ${nextStage}.${program?.enrollment ? " Complete the approval and onboarding requirements below." : ""}` : null;
}

export function ProgramProgressCard({ program, reqItems, opp, ctLabel, attention, onViewMilestones }) {
  const enr = program?.enrollment;
  const stages = [...(opp?.pipeline?.stages || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const idx = stages.findIndex((s) => s.id === opp?.current_stage_id);
  const nextStage = idx > -1 ? stages[idx + 1]?.name : null;
  const total = program?.required_total || 0;
  const done = program?.required_complete || 0;
  const open = reqItems.filter((r) => r.open);
  const overdue = open.filter((r) => r.overdue).length;
  const pending = open.filter((r) => r.status === "ready_for_review").length;
  const gateBlocks = [...(program?.reserve_gate && !program.reserve_gate.ready ? program.reserve_gate.blocking || [] : []),
    ...(program?.career_gate && !program.career_gate.ready ? program.career_gate.blocking || [] : [])];
  const blockers = [...new Set([...open.filter((r) => r.blocked).map((r) => r.title), ...gateBlocks])].slice(0, 5);
  const pct = total ? Math.round((done / total) * 100) : 0;
  const phase = (program?.phases || []).find((p) => p.is_current);
  const notConfigured = enr && (!total || (program.config_status && program.config_status !== "ok"));
  const focus = focusLine(opp?.current_stage_name, nextStage, program);
  const stageAhead = enr && total && done < total && /reserve|career|fta/i.test(opp?.current_stage_name || "");

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="program-progress">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><Target className="w-4 h-4 text-brand" />Program Progress &amp; Career Readiness</h2>
          <p className="text-sm text-slate-500 mt-0.5" data-testid="program-progress-context">
            {programLabel(enr) || ctLabel(opp?.candidate_type) || "No program"}{opp?.current_stage_name ? ` · Stage: ${opp.current_stage_name}` : ""}{phase ? ` · Phase: ${phase.name}` : ""}
          </p>
        </div>
        <Button size="sm" onClick={onViewMilestones} className="bg-brand hover:bg-brand-strong" data-testid="view-all-milestones">View All Milestones <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
      </div>

      {!enr ? (
        <div className="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600" data-testid="program-not-enrolled">
          Not enrolled in a program yet, so there are no qualification requirements to measure.{focus ? ` ${focus}` : ""}
        </div>
      ) : notConfigured ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="program-not-configured">
          <p className="font-semibold">Program requirements not configured</p>
          <p className="text-xs mt-0.5">This person is enrolled, but the program's requirements aren't fully set up or mapped, so progress can't be measured. A zero count here does not mean complete. Review in Milestones, or ask an admin to fix it under Admin Center → Programs.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            <Metric label="Target Career" value={fmtDate(enr.projected_appointment_date) || "Not set"} tone={enr.projected_appointment_date ? "text-slate-900" : "text-amber-700 text-sm"} testId="metric-target-career" />
            <Metric label="Completed" value={`${done} of ${total}`} tone="text-emerald-700" testId="metric-completed" />
            <Metric label="Remaining" value={total - done} testId="metric-remaining" />
            <Metric label="Overdue" value={overdue} tone={overdue ? "text-rose-700" : "text-slate-900"} testId="metric-overdue" />
            <Metric label="Pending verification" value={pending} testId="metric-pending" />
          </div>
          <div className="mt-4" data-testid="readiness">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium text-slate-600">Program readiness: verified required qualifications</span>
              <span className="font-semibold text-slate-900 tabular-nums">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full bg-brand transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
          {stageAhead && <p className="mt-2 text-xs text-slate-500" data-testid="stage-vs-training">The pipeline stage is {opp.current_stage_name}, but {total - done} required qualification{total - done === 1 ? " is" : "s are"} still not verified. A stage change doesn't complete training.</p>}
        </>
      )}

      {focus && enr && !notConfigured && <p className="mt-3 text-sm text-slate-700 flex items-start gap-2"><Flag className="w-4 h-4 text-brand mt-0.5 shrink-0" />{focus}</p>}

      {(blockers.length > 0 || attention.length > 0) && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5" data-testid="program-blockers">
          <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Holding up advancement</p>
          <ul className="text-xs text-amber-800 space-y-0.5 list-disc pl-5">
            {blockers.map((b) => <li key={b}>{b}</li>)}
            {attention.map((a) => <li key={a[0]}>{a[0]}: <span className="text-amber-700">{a[1]}</span></li>)}
          </ul>
        </div>
      )}
    </section>
  );
}
